import { useState, type ReactNode } from "react";
import { MoreInformationSection } from "../sections/MoreInformationSection";
import type { RunRecordBodyProps } from "../../runRecord/RunRecordBody";
import type { DrawerSectionMap } from "./types";
import { CLEAR_DISPOSITIONS } from "../../resultWarnings";
import { formatTime } from "../../formatTime";
import { AnnouncedCountdown } from "~/common/workflows/steps/AnnouncedCountdown";

/** How long the claimed state stays on screen before the host closes it. */
const CLAIM_AUTO_CLOSE_SECONDS = 10;
/** `{seconds}` is substituted live by AnnouncedCountdown. */
const CLAIM_AUTO_CLOSE_LABEL =
  "Closing in {seconds}s — ora will check your Sent Items";
const CLAIM_AUTO_CLOSE_ANNOUNCEMENT =
  "Marked as sent. Closing in 10 seconds — ora will check your Sent Items " +
  "and bring this back if it can't find the email.";

/**
 * PendingEmailSendDrawerBody — the "hand-off" view for an instance parked in
 * ``pending_email_send`` (shared by ora's WorkflowDetailDrawer and the Outlook
 * plugin's InstanceDetailDrawer).
 *
 * The instance has finished all of its Epic work and is waiting for ONE manual
 * action: the AM sends the reply draft from Outlook. The sweep then attaches
 * the sent email to the activity and completes the workflow automatically.
 *
 * So this body must NOT look like a running workflow. Unlike ActiveDrawerBody
 * it shows no live progress bar and leads with a hand-off card stating the work
 * is done and what the one remaining step is. The Steps/Run chips and the
 * artifacts / decision history render below as frozen context.
 *
 * SECOND PATH (email-send-complete-override): when the reply was already
 * handled OUTSIDE the workflow (a phone call, a different thread, a reply
 * from a handset), the sweep will never match and the run would otherwise sit
 * parked for 7 days. A secondary "Reply already handled" action, gated behind
 * a confirm-first dialog naming both things clearing deliberately does NOT do
 * (send, attach), lets the AM finish the run as completed work instead. It
 * rides the SAME `resume_workflow` transport as every other decision
 * (design ADR Constraint 17 / AD-P2) — this body only owns the affordance and
 * the confirm gate; the caller supplies `onClear` and submits it however the
 * host already submits a resume.
 *
 * The disposition list itself lives in `resultWarnings.ts`
 * (`CLEAR_DISPOSITIONS`/`clearDispositionLabel`) — shared with the
 * after-the-fact attribution on the completed body, so the label offered
 * here and the label shown back to the AM afterwards can never drift apart.
 *
 * THIRD PATH ("I sent it" claim, Plan 2): distinct from BOTH paths above.
 * The AM says they already hit Send in Outlook — this is a SOFT, REVERSIBLE
 * hint, not a resolution. Nothing is attached yet: the run only hides from
 * Needs you while the next sent-reply sweep (≤15 min) looks for the email in
 * Sent Items. If the sweep can't find it, the claim is revoked and the card
 * reappears — so the AM may need to press "I sent it" again, or actually
 * send the email. Contrast with the clear path above, which is PERMANENT:
 * clearing finishes the run right now and explicitly attaches nothing, ever.
 * The claim rides the same `resume_workflow` transport too (a second key the
 * decision-token peek recognises, `{claim: "reply_sent"}`) — this body only
 * owns the affordance; the caller supplies `onClaimSent` and submits it
 * however the host already submits a resume.
 */
export interface PendingEmailSendDrawerBodyProps {
  sections: DrawerSectionMap;
  /**
   * ``*_warning`` strings from the fetched result body (extractResultWarnings).
   * These caveats matter MOST at the hand-off: the AM is about to send the
   * reply draft, and e.g. ``edoc_attach_failed_warning`` says the Declarations
   * page could NOT be attached automatically — they must attach it manually
   * BEFORE hitting Send. Rendered as always-visible banners directly under the
   * hand-off card (never inside the collapsible Result section).
   */
  resultWarnings?: string[] | null;
  /**
   * Fallback banner for the "star with no visible warnings" state: rendered in
   * the warnings slot when the caller KNOWS warnings exist (the row's
   * has_warnings flag) but ``resultWarnings`` came back empty — the client-side
   * result fetch fails silently. See ResultWarningsFallback.
   */
  warningsFallback?: ReactNode;
  /**
   * The run's warning lines, each with its own Acknowledge (story 28633: "don't
   * wait to show me the acknowledge links until after the run finishes") — the
   * host's `WarningAcknowledgementList`, the SAME control the finished bodies
   * render. A run parks here with its result (and so its lines) already
   * persisted. When present it REPLACES `resultWarnings` and `warningsFallback`
   * in the same slot: the lines carry the same `*_warning` text, and two copies
   * of one warning is the duplicate the finished bodies already stand down.
   */
  warningLines?: ReactNode | null;
  /**
   * Submits a manual clear (email-send-complete-override). Optional — when
   * absent the body renders exactly as before this story, so every other
   * caller of this shared component is unaffected. The host is responsible
   * for turning this into the transport call (the existing `resume_workflow`
   * hop with a clear disposition — never a new endpoint, AD-P2).
   */
  onClear?: (payload: { disposition: string; note?: string }) => void;
  /** True while a clear submit is in flight — disables the confirm button. */
  clearPending?: boolean;
  /** Error from the most recent failed clear submit — shown inline next to
   *  the confirm controls. */
  clearError?: string | null;
  /**
   * Submits the "I sent it" claim (soft, reversible — see the THIRD PATH
   * note above). Optional — when absent (and `claimedSentAt` is absent too)
   * the body renders exactly as before this story, so every other caller of
   * this shared component is unaffected. The host is responsible for turning
   * this into the transport call (the existing `resume_workflow` hop with
   * `{claim: "reply_sent"}` — never a new endpoint).
   */
  onClaimSent?: () => void;
  /** True while a claim submit is in flight — disables the button and swaps
   *  its label to "Saving…". */
  claimSentPending?: boolean;
  /** Error from the most recent failed claim submit — shown beneath the
   *  button. */
  claimSentError?: string | null;
  /**
   * ISO-8601 timestamp of the currently active claim (status DTO's
   * `reply_claimed_sent_at`), or null/absent when nothing is claimed. Once
   * set, this REPLACES the button with a status line — there is nothing left
   * to click until the claim is revoked (sweep miss) or the run completes
   * (sweep hit).
   */
  claimedSentAt?: string | null;
  /**
   * Close this surface — the HOST's own close, because only the host knows
   * what "close" means (ora dismisses the drawer, the pane goes back to the
   * queue). Supplied → the claimed state shows the shared
   * `AnnouncedCountdown` and calls this once it elapses; absent → the
   * claimed state simply stays put, which is the pre-countdown behaviour.
   *
   * It is an auto-CLOSE, never an auto-answer: the claim was already
   * recorded by the button, and `AnnouncedCountdown`'s action union has no
   * `"submit"` member by design (see its docstring, story 26739).
   */
  onClaimedAutoClose?: () => void;
  /**
   * The phase / milestone main stream (running-drawer). A ReactNode, not a
   * snapshot: the bodies stay presentation-only and none of them imports
   * `phaseModel.ts`, so the adapter keeps its single-reader rule.
   *
   * Rendered FIRST when present, and it SUPPRESSES the flat steps chip it
   * replaces.
   */
  phaseStream?: ReactNode | null;
  /**
   * The step view (story 26739) — the "what do I do right now" pane for this
   * moment in the run. A ReactNode, not a model: the bodies stay
   * presentation-only, so the host derives the view from `push.status` on
   * every render and hands the finished node down.
   *
   * When present it takes the hero position and suppresses the hand-off
   * card's COPY (the reply-ready view says the same thing better) and the flat
   * steps chip. The card's secondary "reply already handled" path survives —
   * it is an affordance, not copy, and the step view deliberately has no
   * send-or-finish control of its own.
   *
   * Default `undefined` reproduces today's markup exactly.
   */
  stepView?: ReactNode | null;
  /**
   * The carrier-evidence decision card (running-drawer). Present ONLY when the
   * run carries an `evidence_decision`, never gated on a status literal —
   * gating on the parked status makes the early-override path unreachable
   * (AC-6). Absent prop reproduces today's markup byte for byte.
   */
  decisionCard?: ReactNode | null;
  /** The run record shown behind the more-information entry point. The SAME
   *  record the standalone /workflows/runs/<id> page renders. */
  record: RunRecordBodyProps;
  /** Copy-id / kill-session links — More information pane only. */
  debugActions?: ReactNode | null;
  /**
   * Host-owned card rendered directly beneath the hand-off card. Today that is
   * ora's local-dev "run the sent-reply sweep" trigger; the slot is a ReactNode
   * for the same reason `decisionCard` and `phaseStream` are — this body stays
   * presentation-only and every host decides for itself whether it has one.
   * Absent by default, which reproduces today's markup exactly.
   */
  sweepCard?: ReactNode | null;
  /** Whether the more-information pane is open. Owned by the top-level
   *  drawer component (survives step/phase updates); the entry-point button
   *  itself lives in the sticky footer, not here. */
  moreInfoOpen?: boolean;
  /** Called when the pane's own back button is clicked. */
  onMoreInfoClose?: () => void;
}

export function PendingEmailSendDrawerBody({
  sections,
  resultWarnings,
  warningsFallback,
  warningLines,
  stepView,
  onClear,
  clearPending = false,
  clearError,
  onClaimSent,
  claimSentPending = false,
  claimSentError,
  claimedSentAt,
  onClaimedAutoClose,
  phaseStream,
  decisionCard,
  record,
  debugActions,
  sweepCard,
  moreInfoOpen = false,
  onMoreInfoClose,
}: PendingEmailSendDrawerBodyProps) {
  const [confirmingClear, setConfirmingClear] = useState(false);
  const [disposition, setDisposition] = useState("");
  const [note, setNote] = useState("");

  const handleConfirmClear = () => {
    // Belt-and-braces: the button below is already disabled without a
    // disposition, but a handler that could ever submit "" must not.
    if (!disposition) return;
    onClear?.({ disposition, note: note.trim() ? note.trim() : undefined });
  };
  // The claim affordance (button or, once claimed, the status line) is an
  // AFFORDANCE like the clear link, not copy — it must survive a step view
  // the same way the clear link does, so it joins that gate below.
  const hasClaimAffordance = Boolean(onClaimSent) || Boolean(claimedSentAt);
  return (
    <>
      {/* The phase / milestone main stream, first and outside every
          collapsible. */}
      {/* The phase stream is the ALWAYS-ON progress display (operator
          directive 2026-09-16): a step view no longer displaces it. Both
          render, stream FIRST, so run progress stays the first thing in the
          body and the step view's reassurance sits under it. */}
      {phaseStream ?? null}
      {stepView ?? null}

      {/* running-drawer: the carrier-evidence decision, in the PRIMARY body.
          Never placed behind the more-information entry point (AC-7). */}
      {decisionCard ?? null}

      {/* running-drawer: the submitted request, in the PRIMARY body — on every
          family, including HITL and terminal (AC-8 / AC-9). */}
      {sections.request ?? null}

      {/* Hand-off card — done in Epic, one manual step left, auto-completes.
          With a step view on, its COPY is suppressed (the reply-ready view has
          already said it, better) and the card shrinks to the secondary
          "already handled" / "I sent it" affordances. With no step view AND
          no clear affordance AND no claim affordance there would be nothing
          left to draw, so it is skipped entirely rather than rendering an
          empty tinted box. */}
      {stepView && !onClear && !hasClaimAffordance ? null : (
      <div
        className={
          stepView
            ? "mb-2 flex flex-col gap-1"
            : "rounded-lg border border-primary-warning-200 bg-primary-warning-50 p-3 mb-2 flex flex-col gap-1"
        }
        data-testid="pending-email-send-handoff"
      >
        {stepView ? null : (
          <>
        <p className="text-sm font-semibold text-primary-warning-800">
          Pending email send
        </p>
        <p className="text-xs text-primary-gray-light-700">
          All Epic work is done. Open the reply draft in your Outlook Drafts and
          hit <span className="font-semibold">Send</span>. The sent email is then
          attached to the activity and this workflow completes automatically — no
          further action is needed here.
        </p>
        <p className="text-2xs text-primary-gray-light-500">
          Bounded to 7 days; if it isn't sent you'll be reminded to send and
          attach it manually.
        </p>
          </>
        )}

        {/* "I sent it" claim — soft and reversible (contrast the clear link
            below, which is permanent). Sits ABOVE the clear collapsible: this
            is what most AMs reach for after hitting Send, while clearing is
            the rarer "handled elsewhere" path. Once claimedSentAt is set the
            button is gone — there is nothing left to click until the sweep
            resolves the claim one way or the other, which is exactly why the
            host may auto-close behind it. */}
        {claimedSentAt ? (
          <div className="flex min-w-0 flex-col gap-1" data-testid="claim-sent-block">
            <p
              className="min-w-0 text-xs text-primary-gray-light-700"
              data-testid="claim-sent-state"
            >
              You said you sent it at{" "}
              <span className="font-semibold">{formatTime(claimedSentAt)}</span>{" "}
              — hidden from Needs you until it&apos;s verified (up to 45 min).
            </p>
            {/* The SHARED countdown (story 26739), not a timer of our own:
                it already owns the tick, the single polite announcement, the
                aria-hidden digits, prefers-reduced-motion, and the Hold
                escape — and its action union deliberately has no "submit"
                member, so an auto-close can never answer anything. Here the
                claim has ALREADY been recorded by the button above; all that
                is left to close is a drawer showing a run that just left the
                queue. No host callback → no countdown, because a countdown
                that closes nothing would be a lie. */}
            {onClaimedAutoClose ? (
              <AnnouncedCountdown
                seconds={CLAIM_AUTO_CLOSE_SECONDS}
                action={{ kind: "close", label: CLAIM_AUTO_CLOSE_LABEL }}
                announcement={CLAIM_AUTO_CLOSE_ANNOUNCEMENT}
                onFire={onClaimedAutoClose}
              />
            ) : null}
          </div>
        ) : (
          onClaimSent && (
            <div className="flex min-w-0 flex-col gap-1">
              {/* Consequence copy — rendered BEFORE the button in DOM order
                  (screen-reader order), same reason the clear flow's
                  consequence copy precedes its confirm button. */}
              <p className="min-w-0 text-2xs text-primary-gray-light-600">
                Hides this from Needs you while we check your Sent Items
                (every 15 minutes). If the email isn&apos;t found, it comes
                back.
              </p>
              <button
                type="button"
                disabled={claimSentPending}
                aria-disabled={claimSentPending}
                onClick={() => onClaimSent()}
                // btn-secondary-gray, the ora 2024 button system's secondary
                // (app/tailwind.css) — NOT the stock Untitled-UI purple
                // `primary-brand-600` this shipped with. Claiming is the
                // ordinary, reversible action; the card's real primary is
                // "press Send in Outlook", which lives outside this UI, so
                // nothing here earns the sunrise fill.
                className="btn-secondary-gray touch-target w-full self-start text-xs font-semibold !px-3 !py-1.5 disabled:opacity-60 sm:w-auto"
                data-testid="claim-sent-button"
              >
                {claimSentPending ? "Saving…" : "I sent it"}
              </button>
              {claimSentError && (
                <span
                  role="alert"
                  className="text-2xs text-primary-error-700"
                  data-testid="claim-sent-error"
                >
                  {claimSentError}
                </span>
              )}
            </div>
          )
        )}

        {/* Secondary path: the reply was already handled outside this
            workflow. Deliberately a link-style action, visually separated
            from the hand-off copy above and never styled as the primary
            action — sending the reply from Outlook is still the expected
            path for almost every run. */}
        {onClear &&
          (confirmingClear ? (
            <div
              className="mt-2 flex flex-col gap-2 rounded-md border border-primary-gray-light-200 bg-base-white p-2"
              role="dialog"
              aria-label="Confirm finishing this run"
              data-testid="pending-email-send-clear-confirm"
            >
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="pending-email-send-clear-disposition"
                  className="text-2xs font-semibold text-primary-gray-light-800"
                >
                  Why is this run finished?
                </label>
                <select
                  id="pending-email-send-clear-disposition"
                  className="text-2xs rounded border border-primary-gray-light-300 px-1.5 py-1"
                  value={disposition}
                  onChange={(e) => setDisposition(e.target.value)}
                  data-testid="pending-email-send-clear-disposition"
                >
                  <option value="" disabled>
                    Choose a reason…
                  </option>
                  {CLEAR_DISPOSITIONS.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
                {!disposition && (
                  <span
                    className="text-2xs text-primary-gray-light-500"
                    data-testid="pending-email-send-clear-disposition-hint"
                  >
                    A reason is required before you can confirm.
                  </span>
                )}
              </div>

              <div className="flex flex-col gap-1">
                <label
                  htmlFor="pending-email-send-clear-note"
                  className="text-2xs font-semibold text-primary-gray-light-800"
                >
                  Note (optional)
                </label>
                <textarea
                  id="pending-email-send-clear-note"
                  className="text-2xs rounded border border-primary-gray-light-300 px-1.5 py-1"
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  data-testid="pending-email-send-clear-note"
                />
              </div>

              {/* Consequence copy — rendered BEFORE the confirm button in DOM
                  order (screen-reader order), not merely positioned near it. */}
              <p className="text-2xs text-primary-warning-800">
                This will not send the reply, and nothing will be attached to
                the activity.
              </p>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setConfirmingClear(false);
                    setDisposition("");
                    setNote("");
                  }}
                  className="lnk-gray text-2xs px-1.5 py-0.5"
                  data-testid="pending-email-send-clear-keep"
                >
                  Keep waiting
                </button>
                <button
                  type="button"
                  disabled={!disposition || clearPending}
                  aria-disabled={!disposition || clearPending}
                  onClick={handleConfirmClear}
                  // btn-error-secondary, not btn-primary and not plain
                  // secondary: by the house rule documented on
                  // HitlChoicePanel.getPillClasses (a destructive choice gets
                  // white bg + red border) this IS the destructive one — it
                  // completes the run permanently, sends no reply and attaches
                  // nothing, exactly as the warning line above says. A sunrise
                  // fill would make the irreversible escape hatch the loudest
                  // control on the card. Was the stock Untitled-UI purple.
                  className="btn-error-secondary text-2xs font-semibold !px-2 !py-0.5 disabled:opacity-60"
                  data-testid="pending-email-send-clear-submit"
                >
                  {clearPending ? "…" : "Confirm"}
                </button>
                {clearError && (
                  <span
                    role="alert"
                    className="text-2xs text-primary-error-700"
                    data-testid="pending-email-send-clear-error"
                  >
                    {clearError}
                  </span>
                )}
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmingClear(true)}
              className="mt-1 self-start text-2xs text-primary-gray-light-700 hover:underline"
              data-testid="pending-email-send-clear"
            >
              Reply already handled — finish this run
            </button>
          ))}
      </div>
      )}

      {/* Directly under the hand-off card it qualifies: the hand-off says the
          send is noticed automatically, and this is what notices it. */}
      {sweepCard ?? null}

      {/* Non-fatal *_warning banners from the result body — surfaced HERE,
          before the AM acts on the hand-off card, because they often qualify
          that exact action (e.g. "the eDoc could not be attached — attach the
          Declarations page manually"). Same testid/markup as the terminal
          bodies' warning banners. The acknowledgeable lines, when the host has
          them, take this slot instead (story 28633). */}
      {warningLines ? (
        <div className="mb-2" data-testid="pending-email-send-warning-lines">
          {warningLines}
        </div>
      ) : null}
      {!warningLines && resultWarnings && resultWarnings.length > 0 && (
        <div
          role="alert"
          className="flex flex-col gap-1 mb-2"
          data-testid="instance-warnings"
        >
          {resultWarnings.map((w, i) => (
            <div
              key={i}
              className="rounded-md bg-primary-warning-50 border border-primary-warning-200 px-3 py-2"
            >
              <p className="text-xs text-primary-warning-700">{w}</p>
            </div>
          ))}
        </div>
      )}
      {!warningLines && (!resultWarnings || resultWarnings.length === 0) && warningsFallback && (
        <div role="alert" className="flex flex-col gap-1 mb-2">
          {warningsFallback}
        </div>
      )}

      {/* The Result renders INSIDE the entry point, via extraBlocks — the
          workflow's outputs are already produced, so the Servicer can review
          them while the reply send is pending. */}
      <MoreInformationSection
        record={record}
        open={moreInfoOpen}
        onClose={() => onMoreInfoClose?.()}
        extraBlocks={
          <>
            {debugActions ?? null}
          </>
        }
      />
    </>
  );
}

export default PendingEmailSendDrawerBody;
